import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { SEOPageLayout, InlineCTA } from "../../components/seo/SEOPageLayout";

const CATEGORIES = [
  {
    name: "Dexcom Continuous Glucose Monitors",
    products: ["Dexcom G7 sensors", "Dexcom G7 receivers", "Dexcom G6 sensors (twin packs and singles)", "Dexcom G6 transmitters"],
    requirements: "Unopened, factory sealed. Minimum 6 months to expiration. Retail packaging with visible expiration date. Not marked Medicaid or Medicare.",
    slug: "dexcom",
  },
  {
    name: "FreeStyle Libre",
    products: ["Libre 3 sensors and readers", "Libre 3 Plus sensors", "Libre 2 sensors and readers", "Libre 14-Day sensors and readers"],
    requirements: "Unopened retail packaging, minimum 6 months to expiration, no NFR (Not For Retail) boxes unless specifically noted.",
    slug: "libre",
  },
  {
    name: "Omnipod",
    products: ["Omnipod 5 pods (10-packs, intro kits, PDM controllers)", "Omnipod DASH pods (5-pack, 10-pack, PDM)", "Omnipod Classic pods and PDM"],
    requirements: "Sealed original boxes, minimum 6 months to expiration.",
    slug: "omnipod",
  },
  {
    name: "Medtronic",
    products: ["MMT-242A advanced sets", "780G pump system", "MiniMed pumps", "Guardian 4 transmitter kits", "Guardian sensors (single and 5-pack)", "Quick-Set pack of 10", "Mio Advanced infusion sets", "Reservoirs box of 10"],
    requirements: "Sealed retail boxes, minimum 6 months to expiration. Not from a Medicaid or Medicare program.",
    slug: "medtronic",
  },
  {
    name: "Tandem Diabetes Care",
    products: ["t:slim pumps", "Autosoft XC infusion sets", "Autosoft 90 infusion sets", "Autosoft infusion sets"],
    requirements: "Unopened retail packaging with expiration date visible.",
    slug: "tandem",
  },
  {
    name: "Test Strips",
    products: ["Accu-Chek (Aviva Plus 100ct/50ct, Guide 100ct/50ct, Smartview)", "OneTouch (Verio 100/50/25, Ultra 100/50/25)", "Contour Next (35ct/50ct/70ct/100ct)", "ReliOn Prime (25ct/50ct/100ct)", "True Metrix (50ct/100ct)", "FreeStyle Lite (50ct/100ct)"],
    requirements: "Sealed boxes only. Minimum 6 months to expiration. No writing or stickers over the barcode.",
    slug: "test-strips",
  },
  {
    name: "Insulin and Injection Supplies",
    products: ["Humulin vials (N, R, 70/30)", "Novolin", "BD needles", "Lancets"],
    requirements: "Sealed, temperature-controlled if applicable. Contact us before transporting insulin to confirm eligibility and handling requirements.",
    slug: "insulin",
  },
];

export default function AcceptedSupplies() {
  return (
    <SEOPageLayout
      title="Accepted Diabetic Supplies | Cash for Test Strips Jacksonville"
      description="See the complete list of accepted diabetic test strips, CGM sensors, and pump supplies. Learn eligibility requirements and how to get started."
      canonical="https://cashforteststripsjacksonville.com/accepted-diabetic-supplies/"
      breadcrumbLabel="Accepted Diabetic Supplies"
      primaryKeyword="Accepted Diabetic Supplies"
      h1="What Diabetic Supplies Do We Accept?"
      intro="Below is the current list of diabetic supplies we accept, organized by category. Every item is subject to condition and expiration checks. If you do not see your brand listed, ask us — the list changes over time."
    >
      {({ openForm }) => (
        <>
          {CATEGORIES.map((cat) => (
            <section key={cat.slug} id={cat.slug} data-testid={`category-${cat.slug}`}>
              <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
                {cat.name}
              </h2>
              <div className="bg-[#F5F5F0] rounded-xl p-6 md:p-7">
                <h3 className="text-sm uppercase tracking-wide text-[#0F5132] font-semibold mb-3">Products accepted</h3>
                <ul className="space-y-2 mb-6">
                  {cat.products.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-[#4B5563]">
                      <CheckCircle className="w-4 h-4 text-[#0F5132] mt-1 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <h3 className="text-sm uppercase tracking-wide text-[#0F5132] font-semibold mb-2">Eligibility requirements</h3>
                <p className="text-[#4B5563] mb-4">{cat.requirements}</p>
                <button onClick={openForm} className="text-[#EA580C] font-semibold underline hover:text-[#C2410C]" data-testid={`cta-${cat.slug}`}>
                  Check if your {cat.name.toLowerCase()} qualify →
                </button>
              </div>
            </section>
          ))}

          <section data-testid="section-general">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>General Eligibility Rules That Apply to Everything</h2>
            <ul className="space-y-2 text-lg text-[#4B5563] list-disc list-inside">
              <li>All boxes must be factory sealed and unopened.</li>
              <li>Minimum six months remaining until expiration.</li>
              <li>Retail packaging in good condition — no water damage, tears, or writing over barcodes.</li>
              <li>Not marked or associated with Medicaid or Medicare.</li>
            </ul>
            <p className="text-lg text-[#4B5563] leading-relaxed mt-4">
              Read the full <Link to="/sell-diabetic-test-strips/" className="text-[#0F5132] font-semibold underline">sell diabetic test strips</Link> page for the complete process.
            </p>
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Not Sure If Your Supplies Qualify?"
            sub="Send us a photo. We will let you know quickly and honestly."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
